/**
 * Notification preferences — per-channel × per-category toggles.
 *
 * Three categories (transactions, loans, investments), three channels
 * (in-app, email, SMS) each. Security alerts are not toggleable and not
 * shown here; the backend always sends them.
 *
 * The save button is debounced — flips toggle immediately on the
 * optimistic UI then PATCHes the backend. If PATCH fails we revert.
 */

import { useEffect, useState } from "react";
import styled from "styled-components";
import { Spin, Switch, message } from "antd";
import {
  WalletOutline,
  CashOutline,
  TrendingUpOutline,
  ShieldCheckmarkOutline,
} from "@raresail/react-ionicons";
import useGet from "../../../hooks/common/useGet";
import useSet from "../../../hooks/common/useSet";
import { API_NOTIFICATION_PREFERENCES } from "../../../constants/apis";
import { Heading } from "../../elements/Elements";
import { PRIMARY_COLOR, primary10 } from "../../../constants/colors";
import { useTitle } from "../../../hooks/common/useTitle";

type Prefs = {
  transactions_in_app: boolean;
  transactions_email: boolean;
  transactions_sms: boolean;
  loans_in_app: boolean;
  loans_email: boolean;
  loans_sms: boolean;
  investments_in_app: boolean;
  investments_email: boolean;
  investments_sms: boolean;
};

const CATEGORIES: {
  key: "transactions" | "loans" | "investments";
  label: string;
  description: string;
  icon: any;
}[] = [
  {
    key: "transactions",
    label: "Transactions",
    description:
      "Deposits, withdrawals, transfers — anything that moves money in or out of your wallet.",
    icon: WalletOutline,
  },
  {
    key: "loans",
    label: "Loans",
    description:
      "Loan status changes, disbursement, repayment reminders and penalties.",
    icon: CashOutline,
  },
  {
    key: "investments",
    label: "Investments",
    description:
      "Investment confirmations, refunds, payouts and AutoInvest events.",
    icon: TrendingUpOutline,
  },
];

const CHANNELS: { key: "in_app" | "email" | "sms"; label: string }[] = [
  { key: "in_app", label: "In-app" },
  { key: "email", label: "Email" },
  { key: "sms", label: "SMS" },
];

export const NotificationPreferencesPage = () => {
  useTitle("Notification Preferences");
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const { loadData, loading } = useGet();
  const { uploadData, submitting } = useSet();

  useEffect(() => {
    loadData({
      api: API_NOTIFICATION_PREFERENCES,
      onSuccess: (data: any) => setPrefs(data),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flip = (
    category: "transactions" | "loans" | "investments",
    channel: "in_app" | "email" | "sms",
  ) => {
    if (!prefs) return;
    const field = `${category}_${channel}` as keyof Prefs;
    const newValue = !prefs[field];
    // Optimistic update
    const next = { ...prefs, [field]: newValue };
    setPrefs(next);

    uploadData({
      api: API_NOTIFICATION_PREFERENCES,
      params: { [field]: newValue },
      method: "PATCH",
      onSuccess: (saved: any) => setPrefs(saved),
    });
  };

  if (loading || !prefs) {
    return (
      <Centered>
        <Spin />
      </Centered>
    );
  }

  return (
    <Wrapper>
      <Heading>Notification Preferences</Heading>
      <Note>
        Choose how Everpesa keeps you in the loop. Toggle the channels you
        want for each category — security alerts are always sent regardless.
      </Note>

      {CATEGORIES.map((cat) => {
        const Icon = cat.icon;
        return (
          <Card key={cat.key}>
            <Header>
              <IconBubble>
                <Icon fontSize={20} color={PRIMARY_COLOR} />
              </IconBubble>
              <div className="flexGrow">
                <div className="font14 semiBold textDark">{cat.label}</div>
                <div className="font12 grey" style={{ lineHeight: 1.4 }}>
                  {cat.description}
                </div>
              </div>
            </Header>

            <Channels>
              {CHANNELS.map((ch) => {
                const field = `${cat.key}_${ch.key}` as keyof Prefs;
                return (
                  <ChannelRow key={ch.key}>
                    <span className="font13 textDark">{ch.label}</span>
                    <Switch
                      checked={prefs[field]}
                      onChange={() => flip(cat.key, ch.key)}
                      disabled={submitting}
                    />
                  </ChannelRow>
                );
              })}
            </Channels>
          </Card>
        );
      })}

      <Card style={{ background: "#f8fafc" }}>
        <Header>
          <IconBubble style={{ background: "#dbeafe" }}>
            <ShieldCheckmarkOutline fontSize={20} color="#2563eb" />
          </IconBubble>
          <div className="flexGrow">
            <div className="font14 semiBold textDark">Security alerts</div>
            <div className="font12 grey" style={{ lineHeight: 1.4 }}>
              Account security messages, password changes and other
              critical alerts are always sent on every channel — these
              cannot be turned off.
            </div>
          </div>
        </Header>
      </Card>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Note = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 4px;
`;
const Card = styled.div`
  background: #fff;
  border: 1px solid ${primary10};
  border-radius: 12px;
  padding: 16px;
`;
const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;
const IconBubble = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${primary10};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`;
const Channels = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 12px;
  padding-left: 48px;
`;
const ChannelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-top: 1px solid #f1f5f9;
`;
const Centered = styled.div`
  display: grid;
  place-items: center;
  min-height: 60vh;
`;

export default NotificationPreferencesPage;
